import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import {
  LogOut,
  RefreshCw,
  Search,
  Mail,
  Phone,
  Building2,
  Users,
  Calendar,
  MessageSquare,
  X,
  Loader2,
  CheckCircle2,
  Trash2,
  Inbox,
  TrendingUp,
  ListChecks,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "contacted", label: "Contacted", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "qualified", label: "Qualified", color: "bg-purple-100 text-purple-700 border-purple-200" },
  { value: "won", label: "Won", color: "bg-green-100 text-green-700 border-green-200" },
  { value: "lost", label: "Lost", color: "bg-slate-100 text-slate-600 border-slate-200" },
];

export default function AdminDashboard() {
  const { logout, authHeader } = useAdminAuth();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.title = "Admin Dashboard – Rydey";
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/quotes`, { headers: authHeader() }),
        axios.get(`${API}/quotes/stats`, { headers: authHeader() }),
      ]);
      setQuotes(q.data || []);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        await logout();
      } else {
        toast.error("Failed to load quotes");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quotes.filter((qt) => {
      if (statusFilter !== "all" && (qt.status || "new") !== statusFilter) return false;
      if (!q) return true;
      return (
        qt.company_name?.toLowerCase().includes(q) ||
        qt.contact_person?.toLowerCase().includes(q) ||
        qt.email?.toLowerCase().includes(q) ||
        qt.phone?.toLowerCase().includes(q)
      );
    });
  }, [quotes, search, statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/quotes/${id}/status`,
        { status },
        { headers: authHeader() }
      );
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Marked as ${status}`);
      // refresh stats lazily
      axios.get(`${API}/quotes/stats`, { headers: authHeader() }).then((r) => setStats(r.data));
    } catch {
      toast.error("Could not update status");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote permanently?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, { headers: authHeader() });
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Quote deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb]" data-testid="admin-dashboard-page">
      {/* Topbar */}
      <header className="bg-[#0D1B40] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="bg-white rounded-lg px-2.5 py-1.5 shadow-md flex items-center" data-testid="admin-home-link">
              <img src="/brand/rydey-logo.png" alt="Rydey" className="h-8 w-auto" />
            </a>
            <div className="hidden sm:block">
              <div className="text-[11px] tracking-[0.18em] font-bold text-[#F5A623] uppercase">
                Admin Console
              </div>
              <div className="text-white text-[15px] font-semibold leading-tight">
                Quote Requests Inbox
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="hidden sm:inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-white/10 hover:bg-white/15 text-[13px] font-medium transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              onClick={async () => {
                await logout();
                window.location.href = "/admin";
              }}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#F5A623] text-[#0D1B40] text-[13px] font-bold hover:bg-[#f7b84a] transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard
            icon={<Inbox size={20} />}
            label="Total Quotes"
            value={stats?.total ?? "—"}
            tone="navy"
          />
          <StatCard
            icon={<TrendingUp size={20} />}
            label="Last 7 Days"
            value={stats?.last_7_days ?? "—"}
            tone="gold"
          />
          <StatCard
            icon={<ListChecks size={20} />}
            label="New / Pending"
            value={stats?.by_status?.new ?? 0}
            tone="blue"
          />
          <StatCard
            icon={<Briefcase size={20} />}
            label="Won Deals"
            value={stats?.by_status?.won ?? 0}
            tone="green"
          />
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search by company, name, email, or phone..."
              className="field-input pl-11"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            <FilterPill
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
              testId="filter-all"
            >
              All ({quotes.length})
            </FilterPill>
            {STATUS_OPTIONS.map((s) => (
              <FilterPill
                key={s.value}
                active={statusFilter === s.value}
                onClick={() => setStatusFilter(s.value)}
                testId={`filter-${s.value}`}
              >
                {s.label} ({stats?.by_status?.[s.value] ?? 0})
              </FilterPill>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-16 flex items-center justify-center">
              <Loader2 className="text-[#0D1B40] animate-spin" size={28} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-slate-500" data-testid="admin-empty-state">
              <Inbox className="mx-auto mb-3 text-slate-300" size={42} />
              <div className="font-semibold text-[#0D1B40] text-[16px] mb-1">No quotes found</div>
              <div className="text-[13.5px]">
                {quotes.length === 0
                  ? "When a company submits the contact form, it'll appear here."
                  : "Try changing your search or filter."}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-quotes-table">
                <thead className="bg-[#f6f7fb] text-left text-[11px] tracking-[0.16em] font-bold text-slate-500 uppercase">
                  <tr>
                    <th className="px-5 py-3.5">Company</th>
                    <th className="px-5 py-3.5">Contact</th>
                    <th className="px-5 py-3.5 hidden md:table-cell">Service</th>
                    <th className="px-5 py-3.5 hidden lg:table-cell">Employees</th>
                    <th className="px-5 py-3.5 hidden md:table-cell">Received</th>
                    <th className="px-5 py-3.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr
                      key={q.id}
                      className="border-t border-slate-100 hover:bg-[#f9fafc] cursor-pointer transition-colors"
                      onClick={() => setSelected(q)}
                      data-testid={`quote-row-${q.id}`}
                    >
                      <td className="px-5 py-4">
                        <div className="font-semibold text-[#0D1B40] text-[14px]">
                          {q.company_name}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-[14px] text-slate-700">{q.contact_person}</div>
                        <div className="text-[12.5px] text-slate-500">{q.email}</div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <div className="text-[13.5px] text-slate-600">{q.service_required}</div>
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell">
                        <div className="text-[13.5px] text-slate-600">{q.employee_count}</div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <div className="text-[13px] text-slate-500">
                          {new Date(q.created_at).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={q.status || "new"} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail Drawer */}
      {selected && (
        <QuoteDetail
          quote={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(s) => updateStatus(selected.id, s)}
          onDelete={() => deleteQuote(selected.id)}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, tone }) {
  const tones = {
    navy: "bg-[#0D1B40] text-white",
    gold: "bg-[#F5A623] text-[#0D1B40]",
    blue: "bg-blue-50 text-[#0D1B40] border border-blue-100",
    green: "bg-green-50 text-[#0D1B40] border border-green-100",
  };
  const iconTones = {
    navy: "bg-[#F5A623]/15 text-[#F5A623]",
    gold: "bg-[#0D1B40]/10 text-[#0D1B40]",
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
  };
  return (
    <div className={`rounded-xl p-5 ${tones[tone]}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconTones[tone]}`}>
          {icon}
        </div>
      </div>
      <div className="text-[28px] font-extrabold leading-none">{value}</div>
      <div className="text-[12px] tracking-[0.16em] font-semibold opacity-75 uppercase mt-2">
        {label}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 px-3.5 py-2 rounded-md text-[12.5px] font-semibold transition-colors ${
        active
          ? "bg-[#0D1B40] text-white"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11.5px] font-semibold ${opt.color}`}
      data-testid={`status-badge-${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {opt.label}
    </span>
  );
}

function QuoteDetail({ quote, onClose, onStatusChange, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="quote-detail-drawer">
      <div className="absolute inset-0 bg-[#0D1B40]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden animate-[fadeUp_0.3s_ease-out]">
        <div className="flex items-start justify-between p-6 border-b border-slate-100">
          <div>
            <div className="text-[11px] tracking-[0.18em] font-bold text-[#F5A623] uppercase mb-1">
              Quote Request
            </div>
            <h2 className="text-[#0D1B40] text-[22px] font-extrabold leading-tight">
              {quote.company_name}
            </h2>
            <div className="text-slate-500 text-[13px] mt-1.5">
              Received{" "}
              {new Date(quote.created_at).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-slate-100 transition-colors"
            data-testid="quote-detail-close"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <DetailRow icon={<Building2 size={16} />} label="Company">
            {quote.company_name}
          </DetailRow>
          <DetailRow icon={<Users size={16} />} label="Contact Person">
            {quote.contact_person}
          </DetailRow>
          <DetailRow icon={<Mail size={16} />} label="Email">
            <a
              href={`mailto:${quote.email}?subject=Re: Your Rydey corporate transport inquiry`}
              className="text-[#0D1B40] hover:text-[#F5A623] transition-colors"
              data-testid="quote-detail-email"
            >
              {quote.email}
            </a>
          </DetailRow>
          <DetailRow icon={<Phone size={16} />} label="Phone">
            <a
              href={`tel:${quote.phone}`}
              className="text-[#0D1B40] hover:text-[#F5A623] transition-colors"
              data-testid="quote-detail-phone"
            >
              {quote.phone}
            </a>
          </DetailRow>
          <DetailRow icon={<Briefcase size={16} />} label="Service Required">
            {quote.service_required}
          </DetailRow>
          <DetailRow icon={<Users size={16} />} label="Employees">
            {quote.employee_count}
          </DetailRow>
          {quote.message && (
            <DetailRow icon={<MessageSquare size={16} />} label="Message">
              <div className="bg-[#f6f7fb] border border-slate-200 rounded-lg p-3 text-[14px] text-slate-700 whitespace-pre-wrap">
                {quote.message}
              </div>
            </DetailRow>
          )}

          <div className="pt-2">
            <div className="text-[11px] tracking-[0.16em] font-bold text-slate-500 uppercase mb-3">
              Update Status
            </div>
            <div className="flex flex-wrap gap-2" data-testid="quote-status-selector">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => onStatusChange(s.value)}
                  className={`px-3.5 py-2 rounded-md text-[12.5px] font-semibold border transition-all ${
                    (quote.status || "new") === s.value
                      ? `${s.color} ring-2 ring-[#0D1B40]/10`
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#0D1B40]/30"
                  }`}
                  data-testid={`status-set-${s.value}`}
                >
                  {(quote.status || "new") === s.value && (
                    <CheckCircle2 size={13} className="inline mr-1 -mt-0.5" />
                  )}
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 p-5 flex items-center justify-between gap-3">
          <button
            onClick={onDelete}
            data-testid="quote-detail-delete"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-red-600 hover:bg-red-50 text-[13px] font-semibold transition-colors"
          >
            <Trash2 size={15} /> Delete
          </button>
          <div className="flex gap-3">
            <a
              href={`mailto:${quote.email}?subject=Re: Your Rydey corporate transport inquiry&body=Hi ${encodeURIComponent(
                quote.contact_person
              )},%0D%0A%0D%0AThanks for reaching out to Rydey. We'd love to put together a custom proposal for ${encodeURIComponent(
                quote.company_name
              )}.%0D%0A%0D%0A`}
              className="btn-ghost-navy text-[13px]"
              style={{ padding: "9px 16px" }}
              data-testid="quote-detail-reply-email"
            >
              <Mail size={15} /> Reply by Email
            </a>
            <a
              href={`tel:${quote.phone}`}
              className="btn-primary text-[13px]"
              style={{ padding: "9px 16px" }}
              data-testid="quote-detail-call"
            >
              <Phone size={15} /> Call Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] tracking-[0.16em] font-bold text-slate-500 uppercase mb-1.5">
        <span className="text-[#F5A623]">{icon}</span>
        {label}
      </div>
      <div className="text-[15px] text-slate-800 pl-7">{children}</div>
    </div>
  );
}
